import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// THE ORGANIZATION'S OWN STRIPE ACCOUNT (#946).
//
// Client for `/organization/payment-account`. The organization comes from the
// session's scope on the server; nothing here names it, and no account id is
// ever sent — whose bank account the money lands in is not the browser's to say.
//
// NEVER THROWS, and ONE FLAT SHAPE per call (this repository compiles with
// `strict: false`, under which a boolean discriminant does not narrow — see
// `organizationName.ts`). `code` is the API's machine code or null; the screen
// turns it into a sentence.
// ─────────────────────────────────────────────────────────────────────

/** Where the account stands, as Stripe reports it. Counts, not field names. */
export interface PaymentAccountStatus {
  detailsSubmitted: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  currentlyDue: number;
  pastDue: number;
  eventuallyDue: number;
  disabledReason: string | null;
  currentDeadline: number | null;
}

export interface PaymentAccountRead {
  /** `null` on success; otherwise a code, e.g. `forbidden`, `not_available`. */
  code: string | null;
  connected: boolean;
  /** Not connected, and an earlier opening never heard back from Stripe (#948). */
  opening: boolean;
  status: PaymentAccountStatus | null;
}

export interface PaymentAccountOpen {
  code: string | null;
  /** True when this call opened it; false when it already existed. */
  opened: boolean;
}

export interface PaymentAccountSession {
  code: string | null;
  clientSecret: string | null;
}

const PATH = '/organization/payment-account';

/** The API's code from a refused response, or a code for the status itself. */
async function codeOf(res: Response): Promise<string> {
  // 404 here means the routes are not mounted: Stripe is not configured in
  // this environment. Said as such, rather than as «not found».
  if (res.status === 404) return 'not_available';
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body.error === 'string' && body.error) return body.error;
  } catch {
    // fall through
  }
  return res.status === 403 ? 'forbidden' : `http_${res.status}`;
}

export async function readPaymentAccount(): Promise<PaymentAccountRead> {
  try {
    const res = await apiFetch(PATH);
    if (!res.ok) return { code: await codeOf(res), connected: false, opening: false, status: null };
    const body = (await res.json()) as {
      connected?: unknown;
      opening?: unknown;
      status?: PaymentAccountStatus;
    };
    return {
      code: null,
      connected: body.connected === true,
      opening: body.connected !== true && body.opening === true,
      status: body.connected === true && body.status ? body.status : null,
    };
  } catch {
    return { code: 'unreachable', connected: false, opening: false, status: null };
  }
}

export async function openPaymentAccount(): Promise<PaymentAccountOpen> {
  try {
    // NO BODY. Deliberately: the server ignores one, and sending nothing makes
    // that impossible to get wrong from this side either.
    const res = await apiFetch(PATH, { method: 'POST' });
    if (!res.ok) return { code: await codeOf(res), opened: false };
    const body = (await res.json()) as { opened?: unknown };
    return { code: null, opened: body.opened === true };
  } catch {
    return { code: 'unreachable', opened: false };
  }
}

/**
 * A fresh client secret for Stripe's embedded components.
 *
 * A 403 `reauthentication_required` is ALSO reported by the transport, which
 * opens the step-up prompt (`lib/api/http.ts`); the caller just shows the code.
 * The secret is held in memory by Connect.js and is never stored here.
 */
export async function createPaymentAccountSession(): Promise<PaymentAccountSession> {
  try {
    const res = await apiFetch(`${PATH}/session`, { method: 'POST' });
    if (!res.ok) return { code: await codeOf(res), clientSecret: null };
    const body = (await res.json()) as { clientSecret?: unknown };
    return typeof body.clientSecret === 'string' && body.clientSecret
      ? { code: null, clientSecret: body.clientSecret }
      : { code: 'invalid_response', clientSecret: null };
  } catch {
    return { code: 'unreachable', clientSecret: null };
  }
}
